import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";
import { Pencil, Plus, X } from "lucide-react";
import { api, type BrokerPosition, type Order, type OrderTotals } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusChip } from "@/components/StatusChip";
import { cn } from "@/lib/utils";
import { DateRangePicker, type DateRange } from "@/components/DateRangePicker";
import { StickySubheader } from "@/components/StickySubheader";
import { usePositionTicks } from "@/hooks/usePositionTicks";
import { floatingTotal } from "@/lib/floating";
import type { PositionTick, QuoteTick } from "@/lib/live";
import { useQuote } from "@/hooks/useQuotes";
import { CostsLine, MoneyDelta, PipsDelta } from "@/components/MoneyDelta";
import { formatDateTime, formatPrice } from "@/lib/format";
import { pushToast } from "@/lib/toast";

const CLOSED_STATES = "closed,cancelled,failed,simulated_closed";
/**
 * M24a. Deliberately `pending` only, not `pending,submitted`: `submitted` means the create
 * call is still in flight, and showing it here with a Cancel button would offer an action
 * the server refuses (it has no broker order id to cancel yet). A row in flight for more
 * than a moment is a divergence for the reconciler to surface, not a button for Andy.
 */
const RESTING_STATES = "pending";

/**
 * M41 — the two books. `live` is the active account: real money, and what Telegram signals
 * trade. `paper` is every other account, which today means the sim-base account the channels
 * are rated on. The server applies the same "active or not" test that execution routing uses,
 * so a row can never be in both books.
 */
export type Book = "live" | "paper";

export function Trades() {
  // M41 — the book lives in the URL (`?book=paper`), so a link can land on it.
  const [searchParams, setSearchParams] = useSearchParams();
  const book: Book = searchParams.get("book") === "paper" ? "paper" : "live";
  // M31c — 60s is the stalled-socket safety net, not the live source; ticks come off the ws.
  const positions = useQuery({ queryKey: queryKeys.positions, queryFn: api.positions, refetchInterval: 60_000 });
  // M27d — the history date window, half-open [from, to) end to end: the picker emits it,
  // the ISO strings carry it, and the server compares closedAt >= from AND closedAt < to.
  const [range, setRange] = useState<DateRange | null>(null);
  const dateParams = range ? { from: range.from.toISOString(), to: range.to.toISOString() } : {};
  const historyStates = book === "paper" ? "simulated_closed" : CLOSED_STATES;
  const history = useQuery({
    queryKey: queryKeys.orders({ state: historyStates, account: book, ...dateParams }),
    // M27b — 200 rather than 50, so a signal's slots rarely straddle the fetch boundary.
    // A signal split across it shows only the fetched slots; honest truncation, keyset later
    // if the history ever outgrows this.
    queryFn: () => api.orders({ state: historyStates, account: book, limit: 200, ...dateParams }),
  });
  // M41 — paper positions are orders, not broker positions: the rating account fills nothing.
  const paperOpen = useQuery({
    queryKey: queryKeys.orders({ state: "simulated_open", account: "paper" }),
    queryFn: () => api.orders({ state: "simulated_open", account: "paper", limit: 200 }),
    enabled: book === "paper",
    refetchInterval: 60_000,
  });
  // M27b — grouped by signal is the DEFAULT: three slots of one signal are one trade idea,
  // not three unrelated rows. The flat list stays one toggle away.
  const [historyView, setHistoryView] = useState<"grouped" | "flat">("grouped");
  // M31 — the live P/L book off the socket; one snapshot per frame, so this re-renders at
  // most once a second while the open tab is showing.
  const ticks = usePositionTicks();
  const resting = useQuery({
    queryKey: queryKeys.orders({ state: RESTING_STATES, account: "live" }),
    queryFn: () => api.orders({ state: RESTING_STATES, account: "live", limit: 50 }),
    // A resting order can fill at any moment, and a filled order must stop offering Cancel.
    refetchInterval: 15_000,
  });
  const restingCount = resting.data?.orders.length ?? 0;
  /**
   * The broker's instrument specs, for their `digits` only — quoting XAUUSD to 2 and EURUSD
   * to 5 is what keeps a price cell a fixed width as the tick moves. Same query and
   * staleTime as the New Trade page, so the two share one cache entry rather than each
   * refetching a list that changes on a spec resync.
   *
   * Empty when the metaapi module is off or the symbol has no spec; `formatPrice` then falls
   * back to stripping dust, which is strictly better than the raw float and never invents
   * precision the instrument does not have.
   */
  const symbolsQuery = useQuery({ queryKey: queryKeys.symbols, queryFn: api.symbols, staleTime: 5 * 60_000 });
  const digitsFor = (symbol: string | undefined): number | null =>
    symbolsQuery.data?.symbols.find((s) => s.symbol === symbol)?.digits ?? null;
  // M33 — controlled so the sticky bar can know which tab is showing: the history filters
  // render inside the bar, and only while History is active.
  const [tab, setTab] = useState("open");
  const setBook = (next: Book) => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next === "paper") params.set("book", "paper");
        else params.delete("book");
        return params;
      },
      { replace: true },
    );
    // Paper has no resting orders to show; do not strand Andy on a tab that is not there.
    if (next === "paper" && tab === "pending") setTab("open");
  };

  return (
    <div className="space-y-4">
      <Tabs value={tab} onValueChange={setTab}>
        <StickySubheader className="mb-4">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold">Trades</h1>
            {book === "live" && (
              <Button size="sm" asChild>
                <Link to="/trade/new">
                  <Plus className="h-4 w-4" /> New trade
                </Link>
              </Button>
            )}
          </div>
          {/* M41 — real money and rated paper are never in the same list. */}
          <div className="flex items-center gap-1.5" role="group" aria-label="Book">
            {(["live", "paper"] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={option === book}
                onClick={() => setBook(option)}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-xs transition-colors",
                  option === book
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border text-muted-foreground",
                )}
              >
                {option === "live" ? "Live account" : "Paper (channel rating)"}
              </button>
            ))}
          </div>
          <TabsList>
            <TabsTrigger value="open">Open</TabsTrigger>
            {/* The count is on the tab, not inside it. The failure this fixes was not knowing
                a resting order existed at all — a tab you must click to discover that is
                barely an improvement on no tab. */}
            {book === "live" && (
              <TabsTrigger value="pending">{restingCount > 0 ? `Pending (${restingCount})` : "Pending"}</TabsTrigger>
            )}
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
          {/* Inside the sticky bar, not the tab panel — these are the filters Andy scrolls
              a long history to escape from, so they are exactly what must stay reachable. */}
          {tab === "history" && (
            <>
              <DateRangePicker value={range} onChange={setRange} />
              <div className="flex flex-wrap items-center gap-1.5">
                {(["grouped", "flat"] as const).map((view) => (
                  <button
                    key={view}
                    type="button"
                    onClick={() => setHistoryView(view)}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs transition-colors",
                      view === historyView
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border text-muted-foreground",
                    )}
                  >
                    {view === "grouped" ? "By signal" : "Flat"}
                  </button>
                ))}
              </div>
            </>
          )}
        </StickySubheader>

        {book === "paper" && (
          <p className="text-xs text-muted-foreground">
            Paper fills of the channels subscribed to the rating account. No money moves, so they are measured in
            pips.{" "}
            <Link to="/channels" className="text-primary underline underline-offset-2">
              Is a channel worth following? Compare channels →
            </Link>
          </p>
        )}

        <TabsContent value="open" className="space-y-2">
          {book === "paper" ? (
            paperOpen.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : paperOpen.isError ? (
              <p className="text-sm text-destructive">Could not load the paper positions.</p>
            ) : paperOpen.data?.orders.length === 0 ? (
              <p className="text-sm text-muted-foreground">No paper positions open.</p>
            ) : (
              paperOpen.data?.orders.map((order) => (
                <PaperOpenRow key={order.id} order={order} digits={digitsFor(order.brokerSymbol)} />
              ))
            )
          ) : positions.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : positions.data && !positions.data.live ? (
            <p className="text-sm text-muted-foreground">Broker not connected — {positions.data.reason}.</p>
          ) : positions.data?.positions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No open positions.</p>
          ) : (
            <>
              <FloatingTotalBar positions={positions.data?.positions ?? []} ticks={ticks} />
              {positions.data?.positions.map((p) => (
                <PositionCard key={p.id} position={p} tick={ticks.get(p.id)} digits={digitsFor(p.symbol)} />
              ))}
            </>
          )}
        </TabsContent>

        <TabsContent value="pending" className="space-y-2">
          {resting.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : restingCount === 0 ? (
            <p className="text-sm text-muted-foreground">
              No resting orders. A limit or stop order waiting to fill would appear here.
            </p>
          ) : (
            resting.data?.orders.map((order) => <RestingRow key={order.id} order={order} />)
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-2">
          {history.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : history.data?.orders.length === 0 ? (
            <p className="text-sm text-muted-foreground">No closed trades yet.</p>
          ) : (
            <>
              <HistoryTotalBar totals={history.data?.totals} paper={book === "paper"} />
              {historyView === "flat" ? (
                history.data?.orders.map((order) => <HistoryRow key={order.id} order={order} />)
              ) : (
                <GroupedHistory orders={history.data?.orders ?? []} />
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * M37 — MT4's History footer: what the filtered window actually came to.
 *
 * The figure is the SERVER's, not a sum of the rows on screen. The list is capped at 200,
 * so summing what arrived would describe the page rather than the window — and would
 * under-report, which is the worst direction to be wrong about a P/L total.
 *
 * Net of commission and swap, matching how a signal's own result is settled. `count` is
 * shown because a total over a truncated list would otherwise be indistinguishable from a
 * total over all of it.
 */
function HistoryTotalBar({ totals, paper = false }: { totals: OrderTotals | undefined; paper?: boolean }) {
  if (!totals || totals.count === 0) return null;
  // M41 — paper closes carry no money by design, so the paper bar never offers a money slot,
  // not even a dash: the measure there is each signal's pips, on the rows below.
  if (paper) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2 text-sm">
        <span className="text-muted-foreground">
          {totals.count} paper {totals.count === 1 ? "close" : "closes"}
        </span>
        <span className="text-xs text-muted-foreground">rated in pips, no money</span>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2">
      <span className="text-sm text-muted-foreground">
        Total P/L{" "}
        <span className="text-xs">
          ({totals.count} {totals.count === 1 ? "trade" : "trades"})
        </span>
      </span>
      {/* Null, not zero: a window of cancelled or simulated closes carries no money, and
          "€0.00" would read as breaking even rather than as nothing to total. */}
      {totals.net === null ? (
        <span className="font-mono text-sm text-muted-foreground">—</span>
      ) : (
        <MoneyDelta value={totals.net} />
      )}
    </div>
  );
}

/** M31 — the MT4-style header line: what the whole open book is worth right now. */
function FloatingTotalBar({
  positions,
  ticks,
}: {
  positions: readonly BrokerPosition[];
  ticks: ReadonlyMap<string, PositionTick>;
}) {
  const total = floatingTotal(positions, ticks);
  if (total === null) return null;
  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2">
      <span className="text-sm text-muted-foreground">Floating P/L</span>
      <MoneyDelta value={total} />
    </div>
  );
}

function PositionCard({
  position,
  tick,
  digits,
}: {
  position: BrokerPosition;
  tick?: PositionTick;
  /** The instrument's own precision, or null when no spec is loaded. See `digitsFor`. */
  digits?: number | null;
}) {
  const queryClient = useQueryClient();
  const [closeOpen, setCloseOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const owned = position.order !== null;
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.positions });
    void queryClient.invalidateQueries({ queryKey: queryKeys.orders() });
  };

  const close = useMutation({
    mutationFn: () => api.closePosition(position.id),
    onSuccess: () => {
      setCloseOpen(false);
      invalidate();
      pushToast(`Close queued for ${position.symbol ?? "position"}`);
    },
  });

  const isSell = position.type?.includes("SELL") ?? false;

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium">
              <span className={isSell ? "text-destructive" : "text-success"}>{isSell ? "SELL" : "BUY"}</span>
              <span className="font-mono">{position.symbol}</span>
              {!owned && (
                <Badge variant="outline" className="text-xs">
                  external
                </Badge>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {position.signalId ? (
                <Link to={`/signals/${position.signalId}`} className="underline">
                  Signal #{position.signalId}
                  {position.slot ? ` · slot ${position.slot}` : ""}
                </Link>
              ) : (
                "Manual position"
              )}
            </div>
          </div>
          {/* M31 — a fresh tick beats the poll; a stale one is a frozen number, not a price. */}
          <MoneyDelta value={tick && !tick.stale && tick.profit !== undefined ? tick.profit : position.profit} />
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
          <Field label="Volume" value={position.volume !== undefined ? String(position.volume) : "—"} />
          <Field label="Open" value={formatPrice(position.openPrice, digits)} />
          <Field
            label="Current"
            value={formatPrice(
              tick && !tick.stale && tick.currentPrice !== undefined ? tick.currentPrice : position.currentPrice,
              digits,
            )}
          />
          <Field
            label="SL / TP"
            value={`${formatPrice(position.stopLoss, digits)} / ${formatPrice(position.takeProfit, digits)}`}
          />
        </div>
        {/* M40a — the cost of holding it, next to what it is making. */}
        <CostsLine swap={position.swap} commission={position.commission} />

        {/* M35 — two icon actions, per Andy: the information is already on the card, the
            row of labelled buttons was noise. Everything editable lives in the one sheet. */}
        <div className="flex items-center justify-end gap-1 pt-1">
          {!owned && (
            <p className="mr-auto text-xs text-muted-foreground">
              External position — it cannot be managed from here yet.
            </p>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            disabled={!owned}
            aria-label="Edit position"
            onClick={() => setEditOpen(true)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-destructive hover:text-destructive"
            disabled={!owned}
            aria-label="Close position"
            onClick={() => setCloseOpen(true)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close this position?</DialogTitle>
            <DialogDescription>
              {position.symbol} · {isSell ? "SELL" : "BUY"} · {position.volume} lots. This is not blocked by the kill
              switch — closing only reduces exposure.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)} disabled={close.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => close.mutate()} disabled={close.isPending}>
              {close.isPending ? "Working…" : "Yes, close it"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mounted only while open so every opening starts from the position's CURRENT stop
          and target, not the values from the last time the sheet was used. */}
      {editOpen && (
        <EditPositionSheet
          position={position}
          tick={tick}
          digits={digits}
          onOpenChange={(next) => {
            if (!next) setEditOpen(false);
          }}
        />
      )}
    </Card>
  );
}

/**
 * M35 — the modify sheet, shaped like MT4's order-modify screen: the live price and P/L at
 * the top, stop and target side by side with one Apply, then partial close with percent
 * shortcuts. One sheet instead of three dialogs, per Andy.
 *
 * Apply submits ONLY the fields that changed. Re-sending an unchanged value is not
 * harmless: the server re-validates each against the live market, so an untouched target
 * could be refused on a move Andy never made. Refusals render beside the field that caused
 * them and the sheet stays open (M24d's rule, carried over).
 */
/**
 * M35 — a percent shortcut's lot size, FLOORED to the broker's 0.01 step. Floor, never
 * round: rounding up would close more than the fraction Andy pressed, and on a small
 * position "50%" quietly becoming two-thirds is real money. Exported for its own tests —
 * the UI harness only exercises the 0.01-lot degenerate case.
 */
export function partialSlice(volume: number, fraction: number): number {
  return Math.floor(volume * fraction * 100) / 100;
}

function EditPositionSheet({
  position,
  tick,
  digits,
  onOpenChange,
}: {
  position: BrokerPosition;
  tick?: PositionTick;
  /** The instrument's own precision, threaded from the card. See `digitsFor`. */
  digits?: number | null;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [slText, setSlText] = useState(String(position.stopLoss ?? ""));
  const [tpText, setTpText] = useState(String(position.takeProfit ?? ""));
  const [slError, setSlError] = useState<string | null>(null);
  const [tpError, setTpError] = useState<string | null>(null);
  const [partialText, setPartialText] = useState("");

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.positions });
    void queryClient.invalidateQueries({ queryKey: queryKeys.orders() });
  };

  const slParsed = Number(slText);
  const tpParsed = Number(tpText);
  const slChanged =
    slText.trim() !== "" && Number.isFinite(slParsed) && slParsed !== (position.stopLoss ?? null);
  const tpChanged =
    tpText.trim() !== "" && Number.isFinite(tpParsed) && tpParsed !== (position.takeProfit ?? null);

  const apply = useMutation({
    // Sequential and error-collecting rather than throwing: with both fields changed, a
    // refused stop must not swallow an acceptable target edit, and vice versa.
    mutationFn: async () => {
      setSlError(null);
      setTpError(null);
      let failed = false;
      const notes: string[] = [];
      if (slChanged) {
        try {
          notes.push((await api.movePositionSl(position.id, slParsed)).note);
        } catch (error) {
          setSlError((error as Error).message);
          failed = true;
        }
      }
      if (tpChanged) {
        try {
          notes.push((await api.movePositionTp(position.id, tpParsed)).note);
        } catch (error) {
          setTpError((error as Error).message);
          failed = true;
        }
      }
      return { failed, notes };
    },
    onSuccess: ({ failed, notes }) => {
      invalidate();
      if (!failed) {
        onOpenChange(false);
        for (const note of notes) pushToast(note);
      }
    },
  });

  const partial = useMutation({
    mutationFn: (volume: number) => api.closePosition(position.id, volume),
    onSuccess: () => {
      onOpenChange(false);
      invalidate();
      pushToast(`Partial close queued for ${position.symbol ?? "position"}`);
    },
  });

  const partialVolume = Number(partialText);
  const partialValid = Number.isFinite(partialVolume) && partialVolume > 0;

  const volume = position.volume ?? 0;

  const isSell = position.type?.includes("SELL") ?? false;
  const livePrice =
    tick && !tick.stale && tick.currentPrice !== undefined ? tick.currentPrice : position.currentPrice;
  const liveProfit = tick && !tick.stale && tick.profit !== undefined ? tick.profit : position.profit;

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isSell ? "SELL" : "BUY"} {position.symbol} · {position.volume} lots
          </DialogTitle>
          <DialogDescription>
            Opened at {formatPrice(position.openPrice, digits)}. Every action here queues through the same
            executor and audit trail as an automatic trade.
          </DialogDescription>
        </DialogHeader>

        {/* The number to modify AGAINST, live — the whole point of an MT4-style sheet. */}
        <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
          <span className="text-muted-foreground">
            Current <span className="font-mono tabular-nums text-foreground">{formatPrice(livePrice, digits)}</span>
          </span>
          <div className="flex flex-col items-end">
            <MoneyDelta value={liveProfit} />
            <CostsLine swap={position.swap} commission={position.commission} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor={`sl-${position.id}`}>
              Stop loss
            </label>
            <Input
              id={`sl-${position.id}`}
              type="number"
              step="any"
              aria-label="Stop loss"
              value={slText}
              onChange={(e) => setSlText(e.target.value)}
              placeholder="none"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor={`tp-${position.id}`}>
              Take profit
            </label>
            <Input
              id={`tp-${position.id}`}
              type="number"
              step="any"
              aria-label="Take profit"
              value={tpText}
              onChange={(e) => setTpText(e.target.value)}
              placeholder="none"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={position.openPrice === undefined}
            onClick={() => setSlText(String(position.openPrice))}
          >
            Breakeven
          </Button>
          <p className="text-xs text-muted-foreground">
            Stops only tighten; targets move either way. The server refuses the rest, with a reason.
          </p>
        </div>
        {slError && <p className="text-sm text-destructive">{slError}</p>}
        {tpError && <p className="text-sm text-destructive">{tpError}</p>}
        <Button
          className="w-full"
          onClick={() => apply.mutate()}
          disabled={apply.isPending || (!slChanged && !tpChanged)}
        >
          {apply.isPending ? "Working…" : "Apply changes"}
        </Button>

        <div className="space-y-2 border-t border-border pt-3">
          <p className="text-xs text-muted-foreground">
            Close part of the position — the rest keeps running with the same stop and target.
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            {([0.25, 0.5, 0.75] as const).map((fraction) => {
              const lots = partialSlice(volume, fraction);
              // A quarter of 0.01 floors to zero, and 75% of 0.02 IS 0.01 of 0.02 remaining…
              // any slice that is nothing, or the whole position, is a doomed request the
              // shortcut must not offer.
              const usable = lots > 0 && lots < volume;
              return (
                <Button
                  key={fraction}
                  size="sm"
                  variant="outline"
                  disabled={!usable || partial.isPending}
                  onClick={() => setPartialText(String(lots))}
                >
                  {fraction * 100}%
                </Button>
              );
            })}
            <Input
              type="number"
              step="any"
              min="0"
              aria-label="Lots to close"
              className="w-28"
              value={partialText}
              onChange={(e) => setPartialText(e.target.value)}
              placeholder="lots"
            />
          </div>
          {partial.isError && (
            <p className="text-sm text-destructive">{(partial.error as Error).message}</p>
          )}
          <Button
            variant="outline"
            className="w-full"
            onClick={() => partial.mutate(partialVolume)}
            disabled={partial.isPending || !partialValid}
          >
            {partial.isPending ? "Working…" : "Close partial"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * M24a — a resting limit or stop order, with the one action it supports.
 *
 * Cancel is keyed by ORDER id: there is no position yet, which is exactly why the
 * `/api/positions/...` buttons on an open position could never reach this row.
 */
function RestingRow({ order }: { order: Order }) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const cancel = useMutation({
    mutationFn: () => api.cancelOrder(order.id),
    onSuccess: () => {
      setConfirmOpen(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders() });
      pushToast(`Cancel queued for ${order.brokerSymbol}`);
    },
  });

  const isSell = order.direction === "sell";
  // "buy limit" / "sell stop" — the broker's own words for what is resting, so what the app
  // calls it and what the terminal calls it are the same thing.
  const kind = order.entryType === null ? "pending order" : `${order.direction} ${order.entryType}`;

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium">
              <span className={isSell ? "text-destructive" : "text-success"}>{isSell ? "SELL" : "BUY"}</span>
              <span className="font-mono">{order.brokerSymbol}</span>
              <Badge variant="outline" className="text-xs">
                {kind}
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground">
              {order.signalId ? (
                <Link to={`/signals/${order.signalId}`} className="underline">
                  Signal #{order.signalId}
                  {order.slot ? ` · slot ${order.slot}` : ""}
                </Link>
              ) : (
                "Manual order"
              )}
            </div>
          </div>
          <StatusChip status={order.state} />
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
          <Field label="Volume" value={order.volume} />
          <Field label="Entry" value={formatPrice(order.requestedPrice)} />
          <Field label="SL / TP" value={`${formatPrice(order.sl)} / ${formatPrice(order.tp)}`} />
          <Field label="Placed" value={formatDateTime(order.createdAt)} />
        </div>

        {/* The broker deletes an expired resting order without ever reporting it (W2.5), so
            the expiry is worth stating where the order is. */}
        {order.expiresAt && (
          <p className="text-xs text-muted-foreground">Expires {formatDateTime(order.expiresAt)}.</p>
        )}

        <div className="pt-1">
          <Button size="sm" variant="destructive" onClick={() => setConfirmOpen(true)}>
            <X className="h-3.5 w-3.5" /> Cancel this order
          </Button>
        </div>
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel this resting order?</DialogTitle>
            <DialogDescription>
              {order.brokerSymbol} · {kind} · {order.volume} lots at {formatPrice(order.requestedPrice)}. Nothing has
              been bought or sold yet, so this removes the order rather than closing a position.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={cancel.isPending}>
              Keep it
            </Button>
            <Button variant="destructive" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
              {cancel.isPending ? "Working…" : "Cancel order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/**
 * M27b — one entry per signal, in the order the signals were last touched (the list arrives
 * newest-order-first, and a group adopts the position of its first-seen order). Manual
 * orders (signalId null) are returned separately and NEVER grouped: "no signal" is not a
 * group, it is the absence of one.
 */
export function groupOrders(list: Order[]): {
  groups: { signalId: number; orders: Order[] }[];
  manual: Order[];
} {
  const bySignal = new Map<number, Order[]>();
  const manual: Order[] = [];
  for (const order of list) {
    if (order.signalId === null) {
      manual.push(order);
      continue;
    }
    const existing = bySignal.get(order.signalId);
    if (existing) existing.push(order);
    else bySignal.set(order.signalId, [order]);
  }
  return { groups: [...bySignal.entries()].map(([signalId, orders]) => ({ signalId, orders })), manual };
}

/**
 * Volumes are numeric strings off the wire ("0.010"); summed as numbers and cleaned to the
 * broker's two-decimal lot grid so 0.01 + 0.01 renders 0.02, not 0.020000000000000004.
 */
export function sumVolume(list: Order[]): number {
  return Number(list.reduce((total, order) => total + Number(order.volume), 0).toFixed(2));
}

function GroupedHistory({ orders }: { orders: Order[] }) {
  const { groups, manual } = groupOrders(orders);
  return (
    <>
      {groups.map((group) => (
        <SignalGroup key={group.signalId} signalId={group.signalId} orders={group.orders} />
      ))}
      {manual.length > 0 && (
        <>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs font-medium text-muted-foreground">Manual trades</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          {manual.map((order) => (
            <HistoryRow key={order.id} order={order} />
          ))}
        </>
      )}
    </>
  );
}

/**
 * A signal's closed slots as one disclosure row. Hand-rolled (button + aria-expanded +
 * conditional render), the HealthStrip pattern — an accordion dependency for one expand is
 * not house style.
 *
 * The header's pips and money come from the SIGNAL row that M27a joins onto each order:
 * orders carry money but no per-order pips column, and the signal's numbers are what
 * settlement wrote. Only the lot size is summed client-side.
 */
function SignalGroup({ signalId, orders }: { signalId: number; orders: Order[] }) {
  const [open, setOpen] = useState(false);
  // Every order in the group carries the same joined summary; the first is as good as any.
  const summary = orders[0]?.signal ?? null;

  return (
    <Card>
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          className="flex w-full items-center justify-between gap-2 p-3 text-left"
        >
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">
              {summary?.channelTitle ?? `Signal #${signalId}`}
              {summary?.ourResult && summary.ourResult !== "none" && (
                <Badge variant="outline" className="ml-2 align-middle text-xs uppercase">
                  {summary.ourResult}
                </Badge>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {orders.length} {orders.length === 1 ? "order" : "orders"} · {sumVolume(orders)} lots
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-0.5 text-sm">
            <PipsDelta value={summary?.pipsRealized} />
            <MoneyDelta value={summary?.profitMoney} />
          </div>
        </button>

        {open && (
          <div className="space-y-2 border-t border-border p-3 pt-2">
            <Link to={`/signals/${signalId}`} className="text-xs text-muted-foreground underline underline-offset-2">
              Signal #{signalId}
            </Link>
            {orders.map((order) => (
              <HistoryRow key={order.id} order={order} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function HistoryRow({ order }: { order: Order }) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between gap-2 p-3 text-sm">
        <div>
          <div className="font-medium">
            {order.brokerSymbol} · {order.direction.toUpperCase()} · {order.volume} lots
          </div>
          <div className="text-xs text-muted-foreground">
            {order.closedAt ? formatDateTime(order.closedAt) : "—"}
            {order.closeReason && ` · ${order.closeReason}`}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <StatusChip status={order.state} />
          <MoneyDelta value={order.profit} />
          <CostsLine swap={order.swap} commission={order.commission} />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * M41 — how far a paper position is from its entry right now, in the pips its settlement will
 * use (the order's own `pipSize`). A buy is closed at the bid and a sell at the ask, as the
 * paper fill engine closes them. Null — rendered as a dash — for a stale or missing tick or an
 * order without a pip size: a frozen number is not a price.
 */
export function floatingPips(order: Order, quote: QuoteTick | undefined): number | null {
  const entry = Number(order.actualOpenPrice ?? order.requestedPrice);
  const pipSize = Number(order.pipSize);
  if (!quote || quote.stale || !Number.isFinite(entry) || !(pipSize > 0)) return null;
  const moved = order.direction === "buy" ? quote.bid - entry : entry - quote.ask;
  return Number((moved / pipSize).toFixed(1));
}

function PaperOpenRow({ order, digits }: { order: Order; digits: number | null }) {
  const quote = useQuote(order.brokerSymbol);
  const isSell = order.direction === "sell";
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium">
              <span className={isSell ? "text-destructive" : "text-success"}>{isSell ? "SELL" : "BUY"}</span>
              <span className="font-mono">{order.brokerSymbol}</span>
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {order.signalId ? (
                <Link to={`/signals/${order.signalId}`} className="underline">
                  {order.signal?.channelTitle ?? `Signal #${order.signalId}`}
                  {order.slot ? ` · slot ${order.slot}` : ""}
                </Link>
              ) : (
                "No signal"
              )}
            </div>
          </div>
          <PipsDelta value={floatingPips(order, quote)} />
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
          <Field label="Entry" value={formatPrice(order.actualOpenPrice ?? order.requestedPrice, digits)} />
          <Field label="Since" value={formatDateTime(order.openedAt ?? order.createdAt)} />
          <Field label="SL / TP" value={`${formatPrice(order.sl, digits)} / ${formatPrice(order.tp, digits)}`} />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * `tabular-nums` and `min-w-0` are load-bearing, not polish: this sits in a `grid-cols-2`,
 * and a proportional digit changes the cell's width on every tick even when the decimal
 * count is fixed. `min-w-0` lets the cell shrink inside the grid track so an unexpectedly
 * long value truncates instead of pushing the card's own width out.
 */
function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <span className="text-muted-foreground">{label}: </span>
      <span className="font-mono tabular-nums text-foreground">{value}</span>
    </div>
  );
}
